import type { ColumnDef } from '@tanstack/react-table';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import {
  createActionsColumn,
  createSerialColumn,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowViewLink } from '@/components/common/RowActions';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { UserListItem } from '../types/user.types';

function renderWrappedText(text: string | null) {
  if (!text) return <EmptyValue />;
  return <WrappingCellText>{text}</WrappingCellText>;
}

export function getUserColumns(
  viewProfileFrom?: string,
  searchText?: string,
): ColumnDef<UserListItem, unknown>[] {
  return [
    createSerialColumn<UserListItem>(),
    {
      accessorKey: 'fullName',
      header: 'Full Name',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.fullName} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'username',
      header: 'Username',
      cell: ({ row }) => (
        <WrappingCellText>
          <HighlightText text={row.original.username} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'hrEmpId',
      header: 'Employee ID',
      cell: ({ row }) => <HighlightText text={row.original.hrEmpId} highlightedText={searchText} />,
    },
    {
      accessorKey: 'mobile',
      header: 'Mobile',
      cell: ({ row }) => <HighlightText text={row.original.mobile} highlightedText={searchText} />,
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => renderWrappedText(row.original.email),
    },
    {
      accessorKey: 'userTypeName',
      header: 'Role',
      cell: ({ row }) => renderWrappedText(row.original.userTypeName),
    },
    {
      accessorKey: 'companyName',
      header: 'Company',
      cell: ({ row }) => renderWrappedText(row.original.companyName),
    },
    {
      accessorKey: 'parentUserName',
      header: 'Reporting To',
      cell: ({ row }) => renderWrappedText(row.original.parentUserName),
    },
    {
      id: 'thana',
      header: 'Thana',
      cell: ({ row }) => {
        const { thanaName, districtName, divisionName } = row.original;
        if (!thanaName) return <EmptyValue />;
        const hasDetails = districtName || divisionName;
        if (!hasDetails) return thanaName;
        return (
          <Tooltip>
            <TooltipTrigger
              render={
                <span
                  tabIndex={0}
                  className="underline decoration-dotted cursor-help focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
                >
                  {thanaName}
                </span>
              }
            />
            <TooltipContent>
              <div className="flex flex-col gap-0.5">
                {districtName && <span>District: {districtName}</span>}
                {divisionName && <span>Division: {divisionName}</span>}
              </div>
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.status === 'active'} />,
    },
    createActionsColumn<UserListItem>((user) => (
      <RowViewLink
        to="/users/view"
        search={{ userId: user.id, from: viewProfileFrom }}
        aria-label={`View ${user.fullName}`}
      />
    )),
  ];
}
